import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as TrashBitIcon } from '@assets/icons/trash-bin.svg';
import { Button } from '@components/button/button';
import { IconButton } from '@components/button/icon-button';
import { StyledDeleteModal } from '@components/delete-modal/delete.styled';
import { t } from 'i18next';
import { ToasterStatus } from 'src/shell/domain/toaster.service';
import { getModalService, getToasterService } from 'src/shell/infrastructure/getters';
import { getStandardService } from 'src/standard/infrastructure/getters';
import { StyledModal } from 'src/ballot/components/update-ballot/update-ballot.styled';

type DependencyItem = {
	id: number;
	title: string;
};

type DependenciesMap = Record<string, DependencyItem[]>;

type ServerErrorResponse = {
	message: string;
	type?: string;
	data?: {
		dependencies?: DependenciesMap;
	};
};

export default function DeleteStandardBtn({ id }: { id: number }) {
	const [isChecking, setIsChecking] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const toasterService = getToasterService();
	const standardService = getStandardService();
	const navigate = useNavigate();
	const modalService = getModalService();

	const parseGraphQlFromMessage = (message: string) => {
		try {
			const parsed = JSON.parse(message);
			return parsed?.errors?.[0];
		} catch {
			return null;
		}
	};

	const getServerResponse = (error: any): ServerErrorResponse => {
		const graphQlError =
			error?.graphQLErrors?.[0] ??
			error?.networkError?.result?.errors?.[0] ??
			error?.cause?.graphQLErrors?.[0] ??
			error?.response?.errors?.[0] ??
			parseGraphQlFromMessage(error?.message ?? '');

		return {
			message: graphQlError?.message ?? error?.message ?? 'Unknown error',
			type: graphQlError?.extensions?.type,
			data: graphQlError?.extensions?.data,
		};
	};

	const deleteStandard = async (standardId: number) => {
		if (isDeleting) {
			return;
		}

		setIsDeleting(true);
		try {
			const response = await standardService.deleteStandard(standardId);
			toasterService.showToast(
				ToasterStatus.success,
				`Standard ${standardId} deleted successfully.`
			);
			navigate('/standards');
		} catch (error: any) {
			const serverResponse = getServerResponse(error);
			const isDeleteForbidden =
				serverResponse.message === 'standard.delete.forbidden' ||
				serverResponse.type === 'Standard delete forbidden' ||
				Boolean(serverResponse.data?.dependencies);

			if (isDeleteForbidden) {
				modalService.openModal(
					<DeleteForbiddenModal dependencies={serverResponse.data?.dependencies ?? {}} />
				);
				return;
			}

			toasterService.showToast(
				ToasterStatus.error,
				`Delete failed. Server response: ${JSON.stringify(serverResponse)}`
			);
			console.error('Delete standard server response:', serverResponse);
		} finally {
			setIsDeleting(false);
		}
	};

	const runDeleteCheck = async (standardId: number) => {
		if (isChecking || isDeleting) {
			return;
		}

		setIsChecking(true);
		try {
			const check = await standardService.canDeleteStandard(standardId);
			if (!check.canDelete) {
				modalService.openModal(<DeleteForbiddenModal dependencies={check.dependencies ?? {}} />);
				return;
			}

			modalService.openModal(
				<DeleteConfirmModal
					onConfirm={async () => {
						await deleteStandard(standardId);
						modalService.closeModal();
					}}
				/>
			);
		} catch (error: any) {
			const serverResponse = getServerResponse(error);
			toasterService.showToast(
				ToasterStatus.error,
				`Delete check failed`
			);
			console.error('Delete standard check server response:', serverResponse);
		} finally {
			setIsChecking(false);
		}
	};

	return (
		<IconButton
			icon={<TrashBitIcon />}
			disabled={isChecking || isDeleting}
			onClick={() => runDeleteCheck(id)}
		/>
	);
}

const DeleteConfirmModal = ({ onConfirm }: { onConfirm: () => Promise<void> | void }) => {
	const modalService = getModalService();
	const [submitting, setSubmitting] = useState(false);

	return (
		<StyledModal>
			<h4>{t('standard.deleteStandard.allowTitle')}</h4>
			<div className="body">{t('standard.deleteStandard.allowText')}</div>
			<div className="buttons">
				<Button
					secondary
					title={t('ballot.updateBallot.deleteBallotCancel')}
					onClick={() => modalService.closeModal()}
				/>
				<Button
					disabled={submitting}
					title={t('standard.deleteStandard.deleteConfirm')}
					onClick={
						() => {
							setSubmitting(true);
							onConfirm();
							// modalService.closeModal();
						}
				}
				/>
			</div>
		</StyledModal>
	);
};

const DeleteForbiddenModal = ({ dependencies }: { dependencies: DependenciesMap }) => {
	const modalService = getModalService();

	return (
		<StyledDeleteModal>
			<div className="title">{t('standard.deleteStandard.title')}</div>
			<div className="description">
				<p>{t('standard.deleteStandard.text')}</p>

				{dependencies.meetings && (
					<div className="dep-pack">
						<b>{t('meeting.meetingList.title')}</b>
						<ul>
							{dependencies.meetings.map(item => (
								<li key={item.id}>
									<a href={`/meetings/${item.id}/general`} target="_blank" rel="noreferrer">
										{item.title}
									</a>
								</li>
							))}
						</ul>
					</div>
				)}

				{dependencies.ballots && (
					<div className="dep-pack">
						<b>{t('ballot.ballotList.title')}</b>
						<ul>
							{dependencies.ballots.map(item => (
								<li key={item.id}>
									<a href={`/ballots/${item.id}/general`} target="_blank" rel="noreferrer">
										{item.title}
									</a>
								</li>
							))}
						</ul>
					</div>
				)}

				{dependencies.catalogItems && (
					<div className="dep-pack">
						<b>{t('common.sidebar.standards.catalogueItems')}</b>
						<ul>
							{dependencies.catalogItems.map(item => (
								<li key={item.id}>
										{item.title}
								</li>
							))}
						</ul>
					</div>
				)}

				{dependencies.catalogItemInternalReferences && (
					<div className="dep-pack">
						<b>{t('common.sidebar.standards.catalogItemInternalReferences')}</b>
						<ul>
							{dependencies.catalogItemInternalReferences.map(item => (
								<li key={item.id}>
									{item.title}
								</li>
							))}
						</ul>
					</div>
				)}




				{dependencies.linkedByRevision && (
					<div className="dep-pack">
						<b>{t('standard.deleteStandard.linkedByRevision')}</b>
						<ul>
							{dependencies.linkedByRevision.map(item => (
								<li key={item.id}>
									<a href={`/standards/detailed/${item.id}/general`} target="_blank" rel="noreferrer">
										{item.title}
									</a>
								</li>
							))}
						</ul>
					</div>
				)}

				{dependencies.linkedByReplacedBy && (
					<div className="dep-pack">
						<b>{t('standard.deleteStandard.linkedByRevisionBy')}</b>
						<ul>
							{dependencies.linkedByReplacedBy.map(item => (
								<li key={item.id}>
									<a href={`/standards/detailed/${item.id}/general`} target="_blank" rel="noreferrer">
										{item.title}
									</a>
								</li>
							))}
						</ul>
					</div>
				)}

				{dependencies.linkedByJustificationConsideredStandards && (
					<div className="dep-pack">
						<b>{t('standard.deleteStandard.linkedByJustificationConsideredStandards')}</b>
						<ul>
							{dependencies.linkedByJustificationConsideredStandards.map(item => (
								<li key={item.id}>
									<a href={`/standards/detailed/${item.id}/general`} target="_blank" rel="noreferrer">
										{item.title}
									</a>
								</li>
							))}
						</ul>
					</div>
				)}

				{/*<pre style={{display: "block", maxWidth: "100%", whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'auto' }}>{JSON.stringify(dependencies, null, 2) }</pre>*/}

			</div>

			<div className="buttons">
				<Button title={t('standard.deleteStandard.closeBtn')} onClick={() => modalService.closeModal()} />
			</div>
		</StyledDeleteModal>
	);
};

